import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Plus, Edit, Trash2, TrendingDown, AlertTriangle, RefreshCw, Calendar, ExternalLink, FileText, Eye, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { usePropiedad } from '../contexts/PropiedadContext';

// Imported components
import { GastoFilters, GastoTable, GastoForm, ProveedorChartModal } from '../components/gastos';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Gastos = () => {
  const { propiedades, propiedadSeleccionada, cambiarPropiedad } = usePropiedad();
  const [searchParams] = useSearchParams();
  const anioParam = searchParams.get('anio') || searchParams.get('year');
  const mesParam = searchParams.get('mes');
  const categoriaParam = searchParams.get('categoria_id') || searchParams.get('categoria');
  const proveedorParam = searchParams.get('proveedor_id') || searchParams.get('proveedor');
  
  // Data states
  const [gastos, setGastos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [mediosPago, setMediosPago] = useState([]);
  const [gastosRecurrentes, setGastosRecurrentes] = useState([]);
  
  // Autocompletado proveedor
  const [proveedorBusqueda, setProveedorBusqueda] = useState('');
  const [mostrarSugerenciasProveedor, setMostrarSugerenciasProveedor] = useState(false);
  
  // Modal states
  const [modalAbierto, setModalAbierto] = useState(false);
  const [modalCategoriaAbierto, setModalCategoriaAbierto] = useState(false);
  const [modalProveedorAbierto, setModalProveedorAbierto] = useState(false);
  const [modalProveedorRecurrenteAbierto, setModalProveedorRecurrenteAbierto] = useState(false);
  const [modalCategoriaRecurrenteAbierto, setModalCategoriaRecurrenteAbierto] = useState(false);
  const [modalRecurrenteAbierto, setModalRecurrenteAbierto] = useState(false);
  const [modalVerRecurrentesAbierto, setModalVerRecurrentesAbierto] = useState(false);
  const [modalDetalleRecurrente, setModalDetalleRecurrente] = useState(false);
  const [modalProveedorChartAbierto, setModalProveedorChartAbierto] = useState(false);
  const [proveedorParaChart, setProveedorParaChart] = useState(null);
  const [recurrenteDetalle, setRecurrenteDetalle] = useState(null);
  const [gastosDelRecurrente, setGastosDelRecurrente] = useState([]);
  const [resumenRecurrente, setResumenRecurrente] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  
  // Editing states
  const [gastoEditando, setGastoEditando] = useState(null);
  const [gastoAEliminar, setGastoAEliminar] = useState(null);
  const [recurrenteEditando, setRecurrenteEditando] = useState(null);
  const [archivoComprobante, setArchivoComprobante] = useState(null);
  
  // Filter states
  const [filtroMes, setFiltroMes] = useState(mesParam || 'todos');
  const [filtroCategoria, setFiltroCategoria] = useState(categoriaParam || 'todas');
  const [filtroProveedor, setFiltroProveedor] = useState(proveedorParam || 'todos');
  const [filtroMedioPago, setFiltroMedioPago] = useState('todos');
  const [filtroAnio, setFiltroAnio] = useState(anioParam || new Date().getFullYear().toString());
  const [filtroProveedorRecurrente, setFiltroProveedorRecurrente] = useState('');
  const [filtroCategoriaRecurrente, setFiltroCategoriaRecurrente] = useState('todas');
  const [mostrarPendientes, setMostrarPendientes] = useState(true);
  const [ordenColumna, setOrdenColumna] = useState('fecha');
  const [ordenDireccion, setOrdenDireccion] = useState('desc');

  // Sincronizar filtros desde los query params de la URL cuando cambien
  useEffect(() => {
    const anio = searchParams.get('anio') || searchParams.get('year');
    const mes = searchParams.get('mes');
    const cat = searchParams.get('categoria_id') || searchParams.get('categoria');
    const prov = searchParams.get('proveedor_id') || searchParams.get('proveedor');

    if (anio) setFiltroAnio(anio);
    if (mes) setFiltroMes(mes);
    if (prov) setFiltroProveedor(prov);
    if (cat) {
      const catObj = categorias.find(c => 
        c.id_categoria === cat || c.nombre.trim().toLowerCase() === cat.trim().toLowerCase()
      );
      setFiltroCategoria(catObj ? catObj.id_categoria : cat);
    }
  }, [searchParams, categorias]);
  
  // Form states
  const [nuevaCategoria, setNuevaCategoria] = useState({ nombre: '', tipo: 'operacional' });
  const [nuevaCategoriaRecurrente, setNuevaCategoriaRecurrente] = useState({ nombre: '', tipo: 'operacional' });
  const [nuevoProveedor, setNuevoProveedor] = useState('');
  const [nuevoProveedorRecurrente, setNuevoProveedorRecurrente] = useState('');
  const [formData, setFormData] = useState({
    fecha: '',
    propiedad_id: '',
    categoria_id: '',
    proveedor: '',
    proveedor_id: '',
    medio_pago_id: '',
    monto: 0,
    notas: '',
    comprobante: null
  });
  const [formRecurrente, setFormRecurrente] = useState({
    dia_cobro: 1,
    categoria_id: '',
    proveedor: '',
    proveedor_id: '',
    medio_pago_id: '',
    monto: 0,
    notas: '',
    frecuencia_meses: 1,
    repeticiones: 12,
    fecha_inicio: new Date().toISOString().split('T')[0]
  });

  // Utility functions
  const formatCurrency = (value) => {
    return `$${Math.round(value || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
  };

  const obtenerNombreCategoria = (id) => {
    return categorias.find(c => c.id_categoria === id)?.nombre || 'N/A';
  };

  const obtenerProveedoresPorCategoria = (categoriaId) => {
    return proveedores.filter(p => p.categoria_id === categoriaId);
  };

  // Filtrar proveedores por coincidencia de nombre
  const proveedoresSugeridos = proveedorBusqueda.trim().length > 0
    ? proveedores.filter(p => 
        p.nombre.toLowerCase().includes(proveedorBusqueda.toLowerCase())
      ).slice(0, 5)
    : [];

  const seleccionarProveedor = (proveedor) => {
    setFormData(prev => ({
      ...prev,
      proveedor: proveedor.nombre,
      proveedor_id: proveedor.id_proveedor,
      categoria_id: proveedor.categoria_id || prev.categoria_id
    }));
    setProveedorBusqueda(proveedor.nombre);
    setMostrarSugerenciasProveedor(false);
  };

  // Data loading
  useEffect(() => {
    if (propiedadSeleccionada) {
      cargarDatos();
    }
  }, [propiedadSeleccionada]);

  // Sync URL search parameters with state
  useEffect(() => {
    const pId = searchParams.get('propiedad_id');
    if (pId && propiedades.length > 0) {
      const exists = propiedades.some(p => p.id_propiedad === pId);
      if (exists && (!propiedadSeleccionada || propiedadSeleccionada.id_propiedad !== pId)) {
        cambiarPropiedad(pId);
      }
    }
  }, [searchParams, propiedades, propiedadSeleccionada, cambiarPropiedad]);

  useEffect(() => {
    const provId = searchParams.get('proveedor_id');
    if (provId) {
      setFiltroProveedor(provId);
    }
    const aParam = searchParams.get('anio');
    if (aParam) {
      setFiltroAnio(aParam);
    }
  }, [searchParams]);

  const cargarDatos = async () => {
    if (!propiedadSeleccionada) return;
    
    try {
      const [resGastos, resCat, resProv, resRecurrentes, resMediosPago] = await Promise.all([
        axios.get(`${API}/gastos?propiedad_id=${propiedadSeleccionada.id_propiedad}`),
        axios.get(`${API}/categorias`),
        axios.get(`${API}/proveedores`),
        axios.get(`${API}/gastos-recurrentes?propiedad_id=${propiedadSeleccionada.id_propiedad}`),
        axios.get(`${API}/medios-pago`)
      ]);
      setGastos(resGastos.data);
      setCategorias(resCat.data);
      setProveedores(resProv.data);
      setGastosRecurrentes(resRecurrentes.data);
      setMediosPago(resMediosPago.data.filter(m => m.activo !== false));
      
      setFormData(prev => ({ ...prev, propiedad_id: propiedadSeleccionada.id_propiedad }));
      
      // No establecer categoría por defecto - dejar vacío
    } catch (error) {
      toast.error('Error al cargar datos');
    }
  };

  // Filtering and sorting
  const aniosDisponibles = [...new Set([
    new Date().getFullYear().toString(),
    filtroAnio,
    ...gastos.map(g => (g.fecha || '').substring(0, 4))
  ].filter(Boolean))].sort((a, b) => b - a);

  const gastosFiltradosSinOrden = gastos.filter(gasto => {
    // Usar la fecha string directamente para evitar problemas de timezone
    const fechaStr = gasto.fecha || '';
    const anioGasto = fechaStr.substring(0, 4);
    const mesGasto = fechaStr.substring(5, 7);
    
    if (!mostrarPendientes && gasto.estado === 'PENDIENTE') return false;
    if (filtroAnio && anioGasto !== filtroAnio) return false;
    if (filtroMes && filtroMes !== 'todos') {
      if (mesGasto !== filtroMes) return false;
    }
    if (filtroCategoria && filtroCategoria !== 'todas') {
      const catObj = categorias.find(c => 
        c.id_categoria === filtroCategoria || c.nombre.trim().toLowerCase() === filtroCategoria.trim().toLowerCase()
      );
      const targetId = catObj ? catObj.id_categoria : filtroCategoria;
      if (gasto.categoria_id !== targetId) return false;
    }
    
    // Filtro por proveedor: comparar por ID o por nombre si no tiene ID
    if (filtroProveedor && filtroProveedor !== 'todos') {
      const proveedorSeleccionado = proveedores.find(p => p.id_proveedor === filtroProveedor);
      if (gasto.proveedor_id) {
        if (gasto.proveedor_id !== filtroProveedor) return false;
      } else if (proveedorSeleccionado) {
        // Si el gasto no tiene proveedor_id, comparar por nombre (case insensitive, trim)
        const nombreGasto = (gasto.proveedor || '').trim().toLowerCase();
        const nombreProveedor = (proveedorSeleccionado.nombre || '').trim().toLowerCase();
        if (nombreGasto !== nombreProveedor) return false;
      } else {
        return false;
      }
    }
    
    if (filtroMedioPago && filtroMedioPago !== 'todos' && gasto.medio_pago_id !== filtroMedioPago) return false;
    return true;
  });

  const ordenarGastos = (gastosArray) => {
    return [...gastosArray].sort((a, b) => {
      let valorA, valorB;
      switch (ordenColumna) {
        case 'fecha':
          valorA = new Date(a.fecha);
          valorB = new Date(b.fecha);
          break;
        case 'categoria':
          valorA = obtenerNombreCategoria(a.categoria_id).toLowerCase();
          valorB = obtenerNombreCategoria(b.categoria_id).toLowerCase();
          break;
        case 'proveedor':
          valorA = (a.proveedor || '').toLowerCase();
          valorB = (b.proveedor || '').toLowerCase();
          break;
        case 'monto':
          valorA = a.monto || 0;
          valorB = b.monto || 0;
          break;
        default:
          valorA = new Date(a.fecha);
          valorB = new Date(b.fecha);
      }
      if (ordenDireccion === 'asc') {
        return valorA > valorB ? 1 : valorA < valorB ? -1 : 0;
      } else {
        return valorA < valorB ? 1 : valorA > valorB ? -1 : 0;
      }
    });
  };

  const gastosFiltrados = ordenarGastos(gastosFiltradosSinOrden);
  const totalGastosFiltrado = gastosFiltrados.reduce((sum, gasto) => sum + (gasto.monto || 0), 0);

  const cambiarOrden = (columna) => {
    if (ordenColumna === columna) {
      setOrdenDireccion(ordenDireccion === 'asc' ? 'desc' : 'asc');
    } else {
      setOrdenColumna(columna);
      setOrdenDireccion('desc');
    }
  };

  const limpiarFiltros = () => {
    setFiltroMes('todos');
    setFiltroCategoria('todas');
    setFiltroProveedor('todos');
    setFiltroMedioPago('todos');
  };

  // Modal handlers
  const abrirModal = (gasto = null) => {
    if (gasto) {
      setGastoEditando(gasto);
      setFormData({
        fecha: gasto.fecha,
        propiedad_id: gasto.propiedad_id,
        categoria_id: gasto.categoria_id,
        proveedor: gasto.proveedor,
        proveedor_id: gasto.proveedor_id || '',
        medio_pago_id: gasto.medio_pago_id || '',
        monto: gasto.monto,
        notas: gasto.notas || '',
        comprobante: gasto.comprobante,
        gasto_recurrente_id: gasto.gasto_recurrente_id || null
      });
    } else {
      setGastoEditando(null);
      setFormData({
        fecha: new Date().toISOString().split('T')[0],
        propiedad_id: propiedadSeleccionada?.id_propiedad || '',
        categoria_id: '',  // Sin categoría por defecto
        proveedor: '',
        proveedor_id: '',
        medio_pago_id: '',
        monto: 0,
        notas: '',
        comprobante: null,
        gasto_recurrente_id: null
      });
    }
    setArchivoComprobante(null);
    setModalAbierto(true);
  };

  // CRUD operations
  const subirComprobante = async () => {
    if (!archivoComprobante) return null;
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', archivoComprobante);
      const response = await axios.post(`${API}/upload-comprobante`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.filename;
    } catch (error) {
      toast.error('Error al subir comprobante');
      return null;
    }
  };

  const guardarGasto = async () => {
    try {
      let comprobanteNombre = formData.comprobante;
      if (archivoComprobante) {
        comprobanteNombre = await subirComprobante();
      }
      const datosGasto = { ...formData, comprobante: comprobanteNombre };
      
      if (gastoEditando) {
        await axios.put(`${API}/gastos/${gastoEditando.id_gasto}`, datosGasto);
        toast.success('Gasto actualizado');
      } else {
        await axios.post(`${API}/gastos`, datosGasto);
        toast.success('Gasto creado');
      }
      cargarDatos();
      setModalAbierto(false);
    } catch (error) {
      toast.error('Error al guardar gasto');
    }
  };

  const confirmarEliminacion = (gasto) => setGastoAEliminar(gasto);

  const eliminarGasto = async () => {
    if (!gastoAEliminar) return;
    try {
      await axios.delete(`${API}/gastos/${gastoAEliminar.id_gasto}`);
      toast.success('Gasto eliminado exitosamente');
      setGastoAEliminar(null);
      cargarDatos();
    } catch (error) {
      toast.error('Error al eliminar el gasto');
    }
  };

  const cambiarEstadoGasto = async (gasto) => {
    const nuevoEstado = gasto.estado === 'PENDIENTE' ? 'PAGADO' : 'PENDIENTE';
    try {
      await axios.patch(`${API}/gastos/${gasto.id_gasto}/estado?estado=${nuevoEstado}`);
      toast.success(`Estado cambiado a ${nuevoEstado === 'PAGADO' ? 'Pagado' : 'Pendiente'}`);
      cargarDatos();
    } catch (error) {
      toast.error('Error al cambiar el estado');
    }
  };

  const verComprobante = (gasto) => {
    if (gasto.comprobante_url) {
      window.open(`${BACKEND_URL}${gasto.comprobante_url}`, '_blank');
    }
  };

  // Category and supplier creation
  const crearNuevaCategoria = async () => {
    if (!nuevaCategoria.nombre.trim()) {
      toast.error('El nombre de la categoría es obligatorio');
      return;
    }
    try {
      const response = await axios.post(`${API}/categorias`, nuevaCategoria);
      toast.success('Categoría creada exitosamente');
      setCategorias([...categorias, response.data]);
      setFormData({ ...formData, categoria_id: response.data.id_categoria });
      setModalCategoriaAbierto(false);
      setNuevaCategoria({ nombre: '', tipo: 'operacional' });
    } catch (error) {
      toast.error('Error al crear categoría');
    }
  };

  const crearNuevoProveedor = async () => {
    if (!nuevoProveedor.trim()) {
      toast.error('Ingresa un nombre para el proveedor');
      return;
    }
    if (!formData.categoria_id) {
      toast.error('Primero selecciona una categoría');
      return;
    }
    try {
      const response = await axios.post(`${API}/proveedores`, {
        nombre: nuevoProveedor.trim(),
        categoria_id: formData.categoria_id
      });
      const nuevo = response.data;
      setProveedores([...proveedores, nuevo]);
      setFormData({ ...formData, proveedor: nuevo.nombre, proveedor_id: nuevo.id_proveedor });
      setNuevoProveedor('');
      setModalProveedorAbierto(false);
      toast.success('Proveedor creado y seleccionado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear proveedor');
    }
  };

  // Create new category from recurring expense modal
  const handleCrearCategoriaRecurrente = async () => {
    if (!nuevaCategoriaRecurrente.nombre.trim()) {
      toast.error('El nombre de la categoría es obligatorio');
      return;
    }
    try {
      const response = await axios.post(`${API}/categorias`, nuevaCategoriaRecurrente);
      toast.success('Categoría creada exitosamente');
      setCategorias([...categorias, response.data]);
      setFormRecurrente({ ...formRecurrente, categoria_id: response.data.id_categoria, proveedor_id: '' });
      setModalCategoriaRecurrenteAbierto(false);
      setNuevaCategoriaRecurrente({ nombre: '', tipo: 'operacional' });
    } catch (error) {
      toast.error('Error al crear categoría');
    }
  };

  // Recurring expense operations
  const guardarGastoRecurrente = async () => {
    if (!formRecurrente.categoria_id || !formRecurrente.proveedor || formRecurrente.monto <= 0) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }
    try {
      if (recurrenteEditando) {
        const response = await axios.put(`${API}/gastos-recurrentes/${recurrenteEditando.id_gasto_recurrente}`, {
          ...formRecurrente,
          propiedad_id: propiedadSeleccionada.id_propiedad
        });
        
        // Mostrar información de cambios si hay
        if (response.data.cambios) {
          const { gastos_actualizados, gastos_creados, gastos_eliminados } = response.data.cambios;
          let mensaje = 'Gasto recurrente actualizado';
          if (gastos_creados > 0) mensaje += ` (+${gastos_creados} gastos creados)`;
          if (gastos_eliminados > 0) mensaje += ` (-${gastos_eliminados} gastos eliminados)`;
          if (gastos_actualizados > 0) mensaje += ` (${gastos_actualizados} actualizados)`;
          toast.success(mensaje);
        } else {
          toast.success('Gasto recurrente actualizado');
        }
      } else {
        await axios.post(`${API}/gastos-recurrentes`, {
          ...formRecurrente,
          propiedad_id: propiedadSeleccionada.id_propiedad
        });
        toast.success('Gasto recurrente creado');
      }
      cerrarModalRecurrente();
      cargarDatos();
    } catch (error) {
      toast.error('Error al guardar gasto recurrente');
    }
  };

  const editarGastoRecurrente = (gr) => {
    setRecurrenteEditando(gr);
    setFormRecurrente({
      dia_cobro: gr.dia_cobro,
      categoria_id: gr.categoria_id,
      proveedor: gr.proveedor,
      proveedor_id: gr.proveedor_id || '',
      medio_pago_id: gr.medio_pago_id || '',
      monto: gr.monto,
      notas: gr.notas || '',
      frecuencia_meses: gr.frecuencia_meses || gr.recurrencia_meses || 1,
      repeticiones: gr.repeticiones,
      fecha_inicio: gr.fecha_inicio
    });
    setModalRecurrenteAbierto(true);
    setModalVerRecurrentesAbierto(false);
    setModalDetalleRecurrente(false);
  };

  const cerrarModalRecurrente = () => {
    setModalRecurrenteAbierto(false);
    setRecurrenteEditando(null);
    setFormRecurrente({
      dia_cobro: 1,
      categoria_id: categorias[0]?.id_categoria || '',
      proveedor: '',
      proveedor_id: '',
      medio_pago_id: '',
      monto: 0,
      notas: '',
      frecuencia_meses: 1,
      repeticiones: 12,
      fecha_inicio: new Date().toISOString().split('T')[0]
    });
  };

  const eliminarGastoRecurrente = async (id) => {
    if (!window.confirm('¿Eliminar este gasto recurrente? También se eliminarán los gastos pendientes asociados.')) return;
    try {
      await axios.delete(`${API}/gastos-recurrentes/${id}`);
      toast.success('Gasto recurrente eliminado');
      cargarDatos();
    } catch (error) {
      toast.error('Error al eliminar gasto recurrente');
    }
  };

  // Ver detalle de un gasto recurrente
  const verDetalleRecurrente = async (gr) => {
    setCargandoDetalle(true);
    setRecurrenteDetalle(gr);
    setModalVerRecurrentesAbierto(false); // Cerrar el modal de lista
    setModalDetalleRecurrente(true);
    try {
      const res = await axios.get(`${API}/gastos-recurrentes/${gr.id_gasto_recurrente}/gastos`);
      setGastosDelRecurrente(res.data.gastos || []);
      setResumenRecurrente(res.data.resumen || null);
    } catch (error) {
      toast.error('Error al cargar detalle');
      setGastosDelRecurrente([]);
      setResumenRecurrente(null);
    } finally {
      setCargandoDetalle(false);
    }
  };

  // Vincular gastos existentes a un gasto recurrente
  const vincularGastosExistentes = async () => {
    if (!recurrenteDetalle) return;
    try {
      const res = await axios.post(`${API}/gastos-recurrentes/${recurrenteDetalle.id_gasto_recurrente}/vincular`);
      toast.success(res.data.message);
      // Recargar el detalle
      verDetalleRecurrente(recurrenteDetalle);
      cargarDatos();
    } catch (error) {
      toast.error('Error al vincular gastos');
    }
  };

  // Editar el monto de un gasto desde el detalle del recurrente
  const editarMontoGastoDetalle = async (gasto) => {
    const nuevoMontoStr = window.prompt(`Ingrese el nuevo monto para el gasto del ${new Date(gasto.fecha + 'T12:00:00').toLocaleDateString('es-CL')}:`, gasto.monto);
    if (nuevoMontoStr === null) return; // Cancelado

    const nuevoMonto = parseFloat(nuevoMontoStr);
    if (isNaN(nuevoMonto) || nuevoMonto < 0) {
      toast.error('Ingrese un monto válido y mayor o igual a cero');
      return;
    }

    try {
      await axios.patch(`${API}/gastos/${gasto.id_gasto}/monto?monto=${nuevoMonto}`);
      // Actualizar el estado local de la lista del modal
      setGastosDelRecurrente(prev => 
        prev.map(g => g.id_gasto === gasto.id_gasto ? { ...g, monto: nuevoMonto } : g)
      );
      // Actualizar la lista principal de gastos sin recargar todo
      setGastos(prev => 
        prev.map(g => g.id_gasto === gasto.id_gasto ? { ...g, monto: nuevoMonto } : g)
      );
      // Actualizar el resumen del modal
      setResumenRecurrente(prev => {
        if (!prev) return prev;
        const diferencia = nuevoMonto - gasto.monto;
        return {
          ...prev,
          monto_total: prev.monto_total + diferencia
        };
      });
      toast.success('Monto actualizado correctamente');
    } catch (error) {
      toast.error('Error al actualizar el monto');
    }
  };

  // Cambiar estado de un gasto desde el detalle del recurrente
  const cambiarEstadoGastoDetalle = async (gasto) => {
    const nuevoEstado = gasto.estado === 'PENDIENTE' ? 'PAGADO' : 'PENDIENTE';
    try {
      await axios.patch(`${API}/gastos/${gasto.id_gasto}/estado?estado=${nuevoEstado}`);
      // Actualizar el estado local de la lista del modal
      setGastosDelRecurrente(prev => 
        prev.map(g => g.id_gasto === gasto.id_gasto ? { ...g, estado: nuevoEstado } : g)
      );
      // Actualizar el resumen del modal
      setResumenRecurrente(prev => {
        if (!prev) return prev;
        const cambio = nuevoEstado === 'PAGADO' ? 1 : -1;
        return {
          ...prev,
          pagados: prev.pagados + cambio,
          pendientes: prev.pendientes - cambio
        };
      });
      // Actualizar la lista principal de gastos sin recargar todo
      setGastos(prev => 
        prev.map(g => g.id_gasto === gasto.id_gasto ? { ...g, estado: nuevoEstado } : g)
      );
      toast.success(`Estado cambiado a ${nuevoEstado === 'PAGADO' ? 'Pagado' : 'Pendiente'}`);
    } catch (error) {
      toast.error('Error al cambiar el estado');
    }
  };

  // Eliminar un gasto desde el detalle del recurrente
  const eliminarGastoDetalle = async (gasto) => {
    if (!window.confirm(`¿Estás seguro de eliminar el gasto del ${new Date(gasto.fecha + 'T12:00:00').toLocaleDateString('es-CL')}?`)) {
      return;
    }
    try {
      await axios.delete(`${API}/gastos/${gasto.id_gasto}`);
      // Actualizar el estado local del modal
      setGastosDelRecurrente(prev => prev.filter(g => g.id_gasto !== gasto.id_gasto));
      // Actualizar el resumen del modal
      setResumenRecurrente(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          total: prev.total - 1,
          pagados: gasto.estado === 'PAGADO' ? prev.pagados - 1 : prev.pagados,
          pendientes: gasto.estado === 'PENDIENTE' ? prev.pendientes - 1 : prev.pendientes,
          monto_total: prev.monto_total - (gasto.monto || 0)
        };
      });
      // Actualizar la lista principal de gastos
      setGastos(prev => prev.filter(g => g.id_gasto !== gasto.id_gasto));
      toast.success('Gasto eliminado correctamente');
    } catch (error) {
      toast.error('Error al eliminar el gasto');
    }
  };

  const handleProveedorClick = (proveedorId, proveedorNombre) => {
    if (!proveedorId) return;
    setProveedorParaChart({ id: proveedorId, nombre: proveedorNombre });
    setModalProveedorChartAbierto(true);
  };

  // Create new supplier from recurring expense modal
  const handleCrearProveedorRecurrente = async () => {
    if (!nuevoProveedorRecurrente.trim()) {
      toast.error('Ingresa un nombre para el proveedor');
      return;
    }
    if (!formRecurrente.categoria_id) {
      toast.error('Primero selecciona una categoría');
      return;
    }
    try {
      const response = await axios.post(`${API}/proveedores`, {
        nombre: nuevoProveedorRecurrente.trim(),
        categoria_id: formRecurrente.categoria_id
      });
      const nuevo = response.data;
      setProveedores([...proveedores, nuevo]);
      setFormRecurrente({ 
        ...formRecurrente, 
        proveedor: nuevo.nombre, 
        proveedor_id: nuevo.id_proveedor 
      });
      setNuevoProveedorRecurrente('');
      setModalProveedorRecurrenteAbierto(false);
      toast.success('Proveedor creado y seleccionado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear proveedor');
    }
  };

  // Filtered providers for recurring form
  const proveedoresFiltradosRecurrente = formRecurrente.categoria_id 
    ? proveedores.filter(p => p.categoria_id === formRecurrente.categoria_id)
    : proveedores;

  if (!propiedadSeleccionada) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-[#FF7F6A] mx-auto mb-4" />
          <p className="text-[#0E3A53] text-lg">Selecciona una propiedad para ver los gastos</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in-up" data-testid="gastos-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-[#0E3A53]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Gastos
          </h1>
          <p className="text-[#0E3A53]/70">Gestiona todos los gastos de tu propiedad</p>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-sm text-[#0E3A53]/60">Total:</span>
            <span className="text-2xl font-bold text-[#FF7F6A]">{formatCurrency(totalGastosFiltrado)}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            data-testid="add-gasto-btn"
            onClick={() => abrirModal()}
            className="bg-gradient-to-r from-[#FF7F6A] to-[#F7E8C9] hover:shadow-lg text-white"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nuevo Gasto
          </Button>
          <Button 
            data-testid="add-gasto-recurrente-btn"
            onClick={() => setModalRecurrenteAbierto(true)}
            variant="outline"
            className="border-[#FF7F6A] text-[#FF7F6A] hover:bg-[#FF7F6A]/10"
          >
            <RefreshCw className="w-5 h-5 mr-2" />
            Gasto Recurrente
          </Button>
          {gastosRecurrentes.length > 0 && (
            <Button 
              data-testid="ver-recurrentes-btn"
              onClick={() => setModalVerRecurrentesAbierto(true)}
              variant="outline"
              className="border-[#1AA1D8] text-[#1AA1D8] hover:bg-[#1AA1D8]/10"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Ver Programados ({gastosRecurrentes.length})
            </Button>
          )}
          <a 
            href="/gasto-rapido"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-cyan-500 text-cyan-600 hover:bg-cyan-50 transition-colors"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Formulario Rápido
          </a>
        </div>
      </div>

      {/* Filters */}
      <GastoFilters
        filtroAnio={filtroAnio}
        setFiltroAnio={setFiltroAnio}
        filtroMes={filtroMes}
        setFiltroMes={setFiltroMes}
        filtroCategoria={filtroCategoria}
        setFiltroCategoria={setFiltroCategoria}
        filtroProveedor={filtroProveedor}
        setFiltroProveedor={setFiltroProveedor}
        filtroMedioPago={filtroMedioPago}
        setFiltroMedioPago={setFiltroMedioPago}
        mostrarPendientes={mostrarPendientes}
        setMostrarPendientes={setMostrarPendientes}
        categorias={categorias}
        proveedores={proveedores}
        mediosPago={mediosPago}
        aniosDisponibles={aniosDisponibles}
        onLimpiarFiltros={limpiarFiltros}
        totalGastos={gastos.length}
        gastosFiltrados={gastosFiltrados.length}
      />

      {/* Table */}
      <div className="card-beach">
        <GastoTable
          gastos={gastosFiltrados}
          categorias={categorias}
          proveedores={proveedores}
          mediosPago={mediosPago}
          ordenColumna={ordenColumna}
          ordenDireccion={ordenDireccion}
          onOrdenar={cambiarOrden}
          onEditar={abrirModal}
          onEliminar={confirmarEliminacion}
          onMarcarPagado={cambiarEstadoGasto}
          onVerComprobante={verComprobante}
          onProveedorClick={handleProveedorClick}
        />
      </div>

      {/* Modals */}
      <GastoForm
        open={modalAbierto}
        onClose={() => setModalAbierto(false)}
        formData={formData}
        setFormData={setFormData}
        gastoEditando={gastoEditando}
        categorias={categorias}
        proveedores={proveedores}
        mediosPago={mediosPago}
        archivoComprobante={archivoComprobante}
        setArchivoComprobante={setArchivoComprobante}
        onGuardar={guardarGasto}
        onProveedorCreado={(nuevo) => setProveedores([...proveedores, nuevo])}
        onCategoriaCreada={(nueva) => setCategorias([...categorias, nueva])}
        onMedioPagoCreado={(nuevo) => setMediosPago([...mediosPago, nuevo])}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!gastoAEliminar} onOpenChange={() => setGastoAEliminar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar gasto?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el gasto de{' '}
              <strong>{formatCurrency(gastoAEliminar?.monto)}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminarGasto} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New Category Modal */}
      <Dialog open={modalCategoriaAbierto} onOpenChange={setModalCategoriaAbierto}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0E3A53]">Nueva Categoría</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={nuevaCategoria.nombre}
                onChange={(e) => setNuevaCategoria({ ...nuevaCategoria, nombre: e.target.value })}
                placeholder="Nombre de la categoría"
              />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={nuevaCategoria.tipo} onValueChange={(v) => setNuevaCategoria({ ...nuevaCategoria, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="operacional">Operacional</SelectItem>
                  <SelectItem value="servicios">Servicios</SelectItem>
                  <SelectItem value="mantenimiento">Mantenimiento</SelectItem>
                  <SelectItem value="otros">Otros</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setModalCategoriaAbierto(false)}>Cancelar</Button>
              <Button onClick={crearNuevaCategoria} className="bg-[#1AA1D8]">Crear</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Supplier Modal */}
      <Dialog open={modalProveedorAbierto} onOpenChange={setModalProveedorAbierto}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0E3A53]">Nuevo Proveedor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={nuevoProveedor}
                onChange={(e) => setNuevoProveedor(e.target.value)}
                placeholder="Nombre del proveedor"
              />
            </div>
            <p className="text-sm text-[#0E3A53]/60">
              Se asociará a la categoría: <strong>{obtenerNombreCategoria(formData.categoria_id)}</strong>
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setModalProveedorAbierto(false)}>Cancelar</Button>
              <Button onClick={crearNuevoProveedor} className="bg-[#1AA1D8]">Crear</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Supplier Modal (from Recurring Expense form) */}
      <Dialog open={modalProveedorRecurrenteAbierto} onOpenChange={setModalProveedorRecurrenteAbierto}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0E3A53]">Nuevo Proveedor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                data-testid="nuevo-proveedor-recurrente-input"
                value={nuevoProveedorRecurrente}
                onChange={(e) => setNuevoProveedorRecurrente(e.target.value)}
                placeholder="Nombre del proveedor"
                onKeyDown={(e) => e.key === 'Enter' && handleCrearProveedorRecurrente()}
              />
            </div>
            <p className="text-sm text-[#0E3A53]/60">
              Se asociará a la categoría: <strong>{obtenerNombreCategoria(formRecurrente.categoria_id)}</strong>
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setModalProveedorRecurrenteAbierto(false)}>Cancelar</Button>
              <Button 
                data-testid="crear-proveedor-recurrente-btn"
                onClick={handleCrearProveedorRecurrente} 
                className="bg-[#1AA1D8]"
              >
                Crear
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Category Modal (from Recurring Expense form) */}
      <Dialog open={modalCategoriaRecurrenteAbierto} onOpenChange={setModalCategoriaRecurrenteAbierto}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0E3A53]">Nueva Categoría</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                data-testid="nueva-categoria-recurrente-input"
                value={nuevaCategoriaRecurrente.nombre}
                onChange={(e) => setNuevaCategoriaRecurrente({ ...nuevaCategoriaRecurrente, nombre: e.target.value })}
                placeholder="Nombre de la categoría"
                onKeyDown={(e) => e.key === 'Enter' && handleCrearCategoriaRecurrente()}
              />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select 
                value={nuevaCategoriaRecurrente.tipo} 
                onValueChange={(v) => setNuevaCategoriaRecurrente({ ...nuevaCategoriaRecurrente, tipo: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="operacional">Operacional</SelectItem>
                  <SelectItem value="servicios">Servicios</SelectItem>
                  <SelectItem value="mantenimiento">Mantenimiento</SelectItem>
                  <SelectItem value="otros">Otros</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setModalCategoriaRecurrenteAbierto(false)}>Cancelar</Button>
              <Button 
                data-testid="crear-categoria-recurrente-btn"
                onClick={handleCrearCategoriaRecurrente} 
                className="bg-[#1AA1D8]"
              >
                Crear
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recurring Expense Modal */}
      <Dialog open={modalRecurrenteAbierto} onOpenChange={cerrarModalRecurrente}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0E3A53]">
              {recurrenteEditando ? 'Editar Gasto Recurrente' : 'Nuevo Gasto Recurrente'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#0E3A53]">Categoría *</Label>
                <div className="flex gap-2">
                  <Select 
                    value={formRecurrente.categoria_id} 
                    onValueChange={(v) => setFormRecurrente({ ...formRecurrente, categoria_id: v, proveedor_id: '' })}
                  >
                    <SelectTrigger className="flex-1"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      {categorias.map(cat => (
                        <SelectItem key={cat.id_categoria} value={cat.id_categoria}>{cat.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setModalCategoriaRecurrenteAbierto(true)}
                    className="border-[#1AA1D8] text-[#1AA1D8] hover:bg-[#1AA1D8]/10 shrink-0"
                    data-testid="add-categoria-recurrente-btn"
                    title="Agregar nueva categoría"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-[#0E3A53]">Proveedor *</Label>
                <div className="flex gap-2">
                  <Select 
                    value={formRecurrente.proveedor_id || '__ninguno__'} 
                    onValueChange={(v) => {
                      if (v === '__ninguno__') {
                        setFormRecurrente({ ...formRecurrente, proveedor_id: '', proveedor: '' });
                      } else {
                        const prov = proveedores.find(p => p.id_proveedor === v);
                        setFormRecurrente({ ...formRecurrente, proveedor_id: v, proveedor: prov?.nombre || '' });
                      }
                    }}
                  >
                    <SelectTrigger className="flex-1"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__ninguno__">-- Sin proveedor --</SelectItem>
                      {proveedoresFiltradosRecurrente.map(prov => (
                        <SelectItem key={prov.id_proveedor} value={prov.id_proveedor}>{prov.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setModalProveedorRecurrenteAbierto(true)}
                    className="border-[#1AA1D8] text-[#1AA1D8] hover:bg-[#1AA1D8]/10 shrink-0"
                    data-testid="add-proveedor-recurrente-btn"
                    disabled={!formRecurrente.categoria_id}
                    title={!formRecurrente.categoria_id ? 'Selecciona una categoría primero' : 'Agregar nuevo proveedor'}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
            <div>
              <Label className="text-[#0E3A53]">Monto *</Label>
              <Input
                type="number"
                value={formRecurrente.monto || ''}
                onChange={(e) => setFormRecurrente({ ...formRecurrente, monto: parseFloat(e.target.value) || 0 })}
                className="text-right"
                placeholder="Ingrese monto"
              />
            </div>
            <div>
              <Label className="text-[#0E3A53]">Medio de Pago</Label>
              <Select 
                value={formRecurrente.medio_pago_id || '__ninguno__'} 
                onValueChange={(v) => setFormRecurrente({ ...formRecurrente, medio_pago_id: v === '__ninguno__' ? '' : v })}
              >
                <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ninguno__">-- Sin especificar --</SelectItem>
                  {mediosPago.map(medio => (
                    <SelectItem key={medio.id_medio_pago} value={medio.id_medio_pago}>{medio.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#0E3A53]">Día del mes</Label>
                <Input
                  type="number"
                  min="1"
                  max="28"
                  value={formRecurrente.dia_cobro}
                  onChange={(e) => setFormRecurrente({ ...formRecurrente, dia_cobro: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div>
                <Label className="text-[#0E3A53]">Fecha inicio</Label>
                <Input
                  type="date"
                  value={formRecurrente.fecha_inicio}
                  onChange={(e) => setFormRecurrente({ ...formRecurrente, fecha_inicio: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#0E3A53]">Cada X meses</Label>
                <Input
                  type="number"
                  min="1"
                  value={formRecurrente.frecuencia_meses}
                  onChange={(e) => setFormRecurrente({ ...formRecurrente, frecuencia_meses: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div>
                <Label className="text-[#0E3A53]">Repeticiones</Label>
                <Input
                  type="number"
                  min="1"
                  value={formRecurrente.repeticiones}
                  onChange={(e) => setFormRecurrente({ ...formRecurrente, repeticiones: parseInt(e.target.value) || 12 })}
                />
              </div>
            </div>
            <div>
              <Label className="text-[#0E3A53]">Notas</Label>
              <Input
                value={formRecurrente.notas}
                onChange={(e) => setFormRecurrente({ ...formRecurrente, notas: e.target.value })}
                placeholder="Descripción adicional"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={cerrarModalRecurrente}>Cancelar</Button>
            <Button onClick={guardarGastoRecurrente} className="bg-gradient-to-r from-[#FF7F6A] to-[#FF5C3A]">
              {recurrenteEditando ? 'Guardar Cambios' : 'Crear Recurrente'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Recurring Expenses Modal */}
      <Dialog open={modalVerRecurrentesAbierto} onOpenChange={setModalVerRecurrentesAbierto}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0E3A53]">Gastos Recurrentes Programados</DialogTitle>
          </DialogHeader>
          <div className="mb-4 flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Input
                type="text"
                placeholder="Buscar proveedor..."
                value={filtroProveedorRecurrente}
                onChange={(e) => setFiltroProveedorRecurrente(e.target.value)}
                className="pl-10"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
            </div>
            <Select 
              value={filtroCategoriaRecurrente} 
              onValueChange={setFiltroCategoriaRecurrente}
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Todas las categorías" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas las categorías</SelectItem>
                {[...categorias].sort((a, b) => a.nombre.localeCompare(b.nombre)).map(cat => (
                  <SelectItem key={cat.id_categoria} value={cat.id_categoria}>
                    {cat.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Tabs defaultValue="activos" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4 bg-[#8FE0FF]/10">
              <TabsTrigger value="activos" className="data-[state=active]:bg-[#1AA1D8] data-[state=active]:text-white">
                Activos ({gastosRecurrentes.filter(g => g.estado_recurrente !== 'VENCIDO').length})
              </TabsTrigger>
              <TabsTrigger value="vencidos" className="data-[state=active]:bg-[#FF7F6A] data-[state=active]:text-white">
                Vencidos ({gastosRecurrentes.filter(g => g.estado_recurrente === 'VENCIDO').length})
              </TabsTrigger>
            </TabsList>
            
            {['activos', 'vencidos'].map(tab => (
              <TabsContent key={tab} value={tab} className="max-h-[55vh] overflow-y-auto pr-2">
                {(() => {
                  const estadoFiltro = tab === 'activos' ? 'ACTIVO' : 'VENCIDO';
                  const filtrados = gastosRecurrentes.filter(gr => {
                    const matchesProveedor = gr.proveedor.toLowerCase().includes(filtroProveedorRecurrente.toLowerCase());
                    const matchesCategoria = filtroCategoriaRecurrente === 'todas' || gr.categoria_id === filtroCategoriaRecurrente;
                    const matchesEstado = estadoFiltro === 'ACTIVO' ? gr.estado_recurrente !== 'VENCIDO' : gr.estado_recurrente === 'VENCIDO';
                    return matchesProveedor && matchesCategoria && matchesEstado;
                  });

                  if (filtrados.length === 0) {
                    return (
                      <div className="text-center py-8 text-[#0E3A53]/60">
                        <RefreshCw className="w-12 h-12 mx-auto mb-2 text-[#0E3A53]/30" />
                        <p>No hay gastos recurrentes {tab} para este filtro</p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-3">
                      {filtrados.map(gr => (
                        <div key={gr.id_gasto_recurrente} className={`p-4 rounded-lg border ${tab === 'vencidos' ? 'bg-gray-50 border-gray-200 opacity-80' : 'bg-[#F7E8C9]/20 border-[#FF7F6A]/20'}`}>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="font-semibold text-[#0E3A53]">
                                {gr.proveedor} 
                                {tab === 'vencidos' && <span className="ml-2 text-xs font-bold text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">FINALIZADO</span>}
                              </p>
                              <p className="text-sm text-[#0E3A53]/60">{obtenerNombreCategoria(gr.categoria_id)}</p>
                              <p className="text-sm text-[#0E3A53]/60">
                                Día {gr.dia_cobro} - Cada {gr.recurrencia_meses || gr.frecuencia_meses} mes(es) - {gr.repeticiones} repeticiones
                              </p>
                              <p className="text-xs text-[#0E3A53]/40 mt-1">
                                {gr.repeticiones_generadas || 0} gastos generados
                              </p>
                            </div>
                            <div className="text-right">
                              <p className={`text-xl font-bold ${tab === 'vencidos' ? 'text-gray-500' : 'text-[#FF7F6A]'}`}>{formatCurrency(gr.monto)}</p>
                              <div className="flex gap-1 mt-2 justify-end">
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={() => verDetalleRecurrente(gr)} 
                                  className="text-purple-600 border-purple-200 hover:bg-purple-50"
                                  title="Ver detalle de gastos"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => editarGastoRecurrente(gr)} className="text-[#1AA1D8]">
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => eliminarGastoRecurrente(gr.id_gasto_recurrente)} className="text-[#FF7F6A]">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </TabsContent>
            ))}
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Modal: Detalle de Gasto Recurrente */}
      <Dialog open={modalDetalleRecurrente} onOpenChange={setModalDetalleRecurrente}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0E3A53]">
              Detalle: {recurrenteDetalle?.proveedor}
            </DialogTitle>
          </DialogHeader>
          
          {cargandoDetalle ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 mx-auto animate-spin text-[#1AA1D8]" />
              <p className="mt-2 text-[#0E3A53]/60">Cargando...</p>
            </div>
          ) : (
            <>
              {/* Resumen */}
              {resumenRecurrente && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">{resumenRecurrente.total}</p>
                    <p className="text-xs text-blue-600/70">Total Gastos</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">{resumenRecurrente.pagados}</p>
                    <p className="text-xs text-green-600/70">Pagados</p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-orange-600">{resumenRecurrente.pendientes}</p>
                    <p className="text-xs text-orange-600/70">Pendientes</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg text-center">
                    <p className="text-lg font-bold text-purple-600">{formatCurrency(resumenRecurrente.monto_total)}</p>
                    <p className="text-xs text-purple-600/70">Monto Total</p>
                  </div>
                </div>
              )}

              {/* Información del recurrente */}
              <div className="p-3 bg-gray-50 rounded-lg mb-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  <div>
                    <span className="text-[#0E3A53]/60">Día de cobro:</span>
                    <span className="ml-1 font-medium">{recurrenteDetalle?.dia_cobro}</span>
                  </div>
                  <div>
                    <span className="text-[#0E3A53]/60">Frecuencia:</span>
                    <span className="ml-1 font-medium">Cada {recurrenteDetalle?.frecuencia_meses || recurrenteDetalle?.recurrencia_meses} mes(es)</span>
                  </div>
                  <div>
                    <span className="text-[#0E3A53]/60">Repeticiones:</span>
                    <span className="ml-1 font-medium">{recurrenteDetalle?.repeticiones}</span>
                  </div>
                  <div>
                    <span className="text-[#0E3A53]/60">Monto:</span>
                    <span className="ml-1 font-medium text-[#FF7F6A]">{formatCurrency(recurrenteDetalle?.monto)}</span>
                  </div>
                </div>
              </div>

              {/* Lista de gastos */}
              <div className="border rounded-lg overflow-hidden">
                {gastosDelRecurrente.length === 0 ? (
                  <div className="p-4 text-center">
                    <p className="text-[#0E3A53]/60 mb-3">No hay gastos vinculados a este gasto recurrente</p>
                    <Button 
                      onClick={vincularGastosExistentes}
                      variant="outline"
                      className="text-purple-600 border-purple-200"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Vincular gastos existentes
                    </Button>
                    <p className="text-xs text-[#0E3A53]/40 mt-2">
                      Busca y vincula gastos del mismo proveedor con fecha cercana al día {recurrenteDetalle?.dia_cobro}
                    </p>
                  </div>
                ) : (
                  <div className="bg-gray-50 border-b p-3 flex justify-between items-center">
                    <p className="text-sm text-[#0E3A53]/70 font-medium">Historial de gastos</p>
                    <Button 
                      onClick={vincularGastosExistentes}
                      variant="outline"
                      size="sm"
                      className="text-purple-600 border-purple-200 bg-white"
                      title="Buscar y vincular gastos que hayan perdido su enlace"
                    >
                      <RefreshCw className="w-3.5 h-3.5 mr-1" />
                      Vincular gastos
                    </Button>
                  </div>
                )}
                {gastosDelRecurrente.length > 0 && (
                  <table className="w-full text-sm">
                    <thead className="bg-[#0E3A53] text-white">
                      <tr>
                        <th className="p-2 text-left">Fecha</th>
                        <th className="p-2 text-left hidden md:table-cell">Categoría</th>
                        <th className="p-2 text-right">Monto</th>
                        <th className="p-2 text-center">Estado</th>
                        <th className="p-2 text-center w-12">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gastosDelRecurrente.map((gasto, idx) => (
                        <tr key={gasto.id_gasto} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="p-2">{new Date(gasto.fecha + 'T12:00:00').toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                          <td className="p-2 hidden md:table-cell text-[#0E3A53]/60">{obtenerNombreCategoria(gasto.categoria_id)}</td>
                          <td className="p-2 text-right font-medium">
                            <div className="flex items-center justify-end gap-2">
                              {formatCurrency(gasto.monto)}
                              <button
                                onClick={() => editarMontoGastoDetalle(gasto)}
                                className="text-gray-400 hover:text-[#1AA1D8] transition-colors"
                                title="Editar monto"
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                          <td className="p-2 text-center">
                            <button
                              onClick={() => cambiarEstadoGastoDetalle(gasto)}
                              className={`px-2 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                                gasto.estado === 'PAGADO' 
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                  : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                              }`}
                              title={gasto.estado === 'PAGADO' ? 'Clic para marcar como pendiente' : 'Clic para marcar como pagado'}
                            >
                              {gasto.estado}
                            </button>
                          </td>
                          <td className="p-2 text-center">
                            <button
                              onClick={() => eliminarGastoDetalle(gasto)}
                              className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                              title="Eliminar gasto"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Botones de acción */}
              <div className="flex justify-between mt-4 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setModalDetalleRecurrente(false);
                    editarGastoRecurrente(recurrenteDetalle);
                  }}
                  className="text-[#1AA1D8]"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Editar Configuración
                </Button>
                <Button variant="outline" onClick={() => setModalDetalleRecurrente(false)}>
                  Cerrar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* Modal for Provider Chart */}
      <ProveedorChartModal
        open={modalProveedorChartAbierto}
        onClose={() => {
          setModalProveedorChartAbierto(false);
          setProveedorParaChart(null);
        }}
        proveedorId={proveedorParaChart?.id}
        proveedorNombre={proveedorParaChart?.nombre}
        propiedades={propiedades}
      />
    </div>
  );
};

export default Gastos;
